import React, { useState } from 'react';
import { Upload, X, FileJson } from 'lucide-react';
import './ImportModal.css';

interface Group {
    _id: string;
    name: string;
    color: string;
}

interface ImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: () => Promise<void>;
    onFilesSelect: (files: File[]) => void;
    selectedFiles: File[];
    type: 'deck' | 'subdeck';
    importing: boolean;
    groups?: Group[];
    selectedGroupId?: string | null;
    onGroupChange?: (groupId: string | null) => void;
    // Aceita também .zip (JSON + imagens) — só nos fluxos que passam pelo exportImportService
    acceptZip?: boolean;
    // Texto exibido no botão durante a importação (ex.: "Enviando imagens 12/30")
    progressLabel?: string;
}

const ImportModal: React.FC<ImportModalProps> = ({
    isOpen, onClose, onImport, onFilesSelect, selectedFiles, type, importing,
    groups, selectedGroupId, onGroupChange, acceptZip = false, progressLabel
}) => {
    const [dragActive, setDragActive] = useState(false);

    if (!isOpen) return null;

    // Importação múltipla só faz sentido para subdecks — um deck já carrega todos os seus subdecks num único arquivo
    const isMultiple = type === 'subdeck';

    const addFiles = (incoming: File[]) => {
        const accepted = incoming.filter(f =>
            f.type === 'application/json' || (acceptZip && (/\.zip$/i.test(f.name) || /zip/i.test(f.type)))
        );
        if (!accepted.length) return;
        onFilesSelect(isMultiple ? [...selectedFiles, ...accepted] : accepted.slice(0, 1));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(e.target.files || []));
        e.target.value = '';
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        addFiles(Array.from(e.dataTransfer.files || []));
    };

    const removeFile = (index: number) => {
        onFilesSelect(selectedFiles.filter((_, i) => i !== index));
    };

    const handleImport = async () => {
        if (!selectedFiles.length) return;
        await onImport();
    };

    const handleClose = () => {
        if (!importing) {
            onFilesSelect([]);
            onClose();
        }
    };

    const typeName = type === 'deck' ? 'Deck' : 'Subdeck';
    const formatLabel = acceptZip ? 'JSON ou ZIP' : 'JSON';
    const importLabel = selectedFiles.length > 1 ? `${selectedFiles.length} ${typeName}s` : typeName;

    return (
        <div className="im-overlay" onClick={handleClose}>
            <div className="im-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    className="im-close-btn"
                    onClick={handleClose}
                    disabled={importing}
                    title="Fechar"
                >
                    <X size={20} />
                </button>

                <div className="im-header">
                    <FileJson size={32} className="im-header-icon" />
                    <h2>Importar {typeName}</h2>
                </div>

                <p className="im-description">
                    Selecione {isMultiple ? 'um ou mais arquivos' : 'um arquivo'} {formatLabel} exportado{isMultiple ? 's' : ''} anteriormente.
                    {acceptZip && ' O .zip deve ter um único .json na raiz e as imagens referenciadas por ele.'}
                    {type === 'deck' ? ' O deck será criado com todos os subdecks e cards.' : ' Cada arquivo vira um subdeck com todos os seus cards.'}
                </p>

                {/* Área de Upload */}
                <div
                    className={`im-upload-area ${dragActive ? 'drag-active' : ''} ${selectedFiles.length ? 'has-file' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    {!selectedFiles.length && (
                        <>
                            <Upload size={40} className="im-upload-icon" />
                            <p className="im-upload-text">
                                Arraste {isMultiple ? 'os arquivos' : 'o arquivo'} aqui ou clique para selecionar
                            </p>
                            <label htmlFor="import-file" className="im-file-label">
                                Escolher arquivo{isMultiple ? 's' : ''} {formatLabel}
                            </label>
                        </>
                    )}

                    <input
                        id="import-file"
                        type="file"
                        accept={acceptZip ? '.json,.zip' : '.json'}
                        multiple={isMultiple}
                        onChange={handleFileChange}
                        className="im-file-input"
                        disabled={importing}
                    />

                    {selectedFiles.length > 0 && (
                        <div className="im-file-list">
                            {selectedFiles.map((file, index) => (
                                <div key={`${file.name}-${file.size}-${index}`} className="im-file-selected">
                                    <FileJson size={40} className="im-file-icon" />
                                    <div className="im-file-info">
                                        <div className="im-file-name">{file.name}</div>
                                        <div className="im-file-size">
                                            {(file.size / 1024).toFixed(2)} KB
                                        </div>
                                    </div>
                                    {!importing && (
                                        <button
                                            className="im-file-remove"
                                            onClick={() => removeFile(index)}
                                            title="Remover arquivo"
                                        >
                                            <X size={20} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {isMultiple && !importing && (
                                <label htmlFor="import-file" className="im-file-label im-file-add-more">
                                    + Adicionar mais arquivos
                                </label>
                            )}
                        </div>
                    )}
                </div>

                {/* Seletor de grupo (apenas para subdeck) */}
                {type === 'subdeck' && groups && groups.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #111)' }}>
                            Adicionar ao grupo
                        </label>
                        <select
                            value={selectedGroupId || ''}
                            onChange={e => onGroupChange?.(e.target.value || null)}
                            disabled={importing}
                            style={{
                                width: '100%', padding: '9px 12px', borderRadius: 8,
                                border: '1.5px solid rgba(13,59,14,0.2)', fontSize: 14,
                                background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #111)',
                                fontFamily: 'inherit', cursor: 'pointer'
                            }}
                        >
                            <option value="">Sem grupo</option>
                            {groups.map(g => (
                                <option key={g._id} value={g._id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Ações */}
                <div className="im-actions">
                    <button
                        onClick={handleClose}
                        className="im-btn-secondary"
                        disabled={importing}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleImport}
                        className="im-btn-primary"
                        disabled={!selectedFiles.length || importing}
                    >
                        {importing ? (
                            <>
                                <span className="im-spinner"></span>
                                {progressLabel || 'Importando...'}
                            </>
                        ) : (
                            <>
                                <Upload size={16} />
                                Importar {importLabel}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportModal;
